'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CommonFactorCalculator() {
  const t = useTranslations("tools.commonFactorCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numbersInput, setNumbersInput] = useState('330, 75, 450, 225');
  const [results, setResults] = useState<{
    gcf: number;
    commonFactors: number[];
    numbers: Array<{ value: number; factors: number[] }>;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const getFactors = (num: number): number[] => {
    const factors: number[] = [];
    for (let i = 1; i <= Math.sqrt(num); i++) {
      if (num % i === 0) {
        factors.push(i);
        if (i !== num / i) {
          factors.push(num / i);
        }
      }
    }
    return factors.sort((a, b) => a - b);
  };

  const gcd = (a: number, b: number): number => {
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    return a;
  };

  const calculateGCF = (numbers: number[]): number => {
    if (numbers.length === 0) return 0;
    let result = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      result = gcd(result, numbers[i]);
    }
    return result;
  };

  const getCommonFactors = (numbers: number[]): number[] => {
    if (numbers.length === 0) return [];
    const firstFactors = getFactors(numbers[0]);
    const commonFactors = firstFactors.filter(factor => {
      return numbers.every(num => num % factor === 0);
    });
    return commonFactors.sort((a, b) => a - b);
  };

  const parseInput = (input: string): number[] => {
    const parts = input.split(',').map(s => s.trim()).filter(s => s !== '');
    const numbers: number[] = [];

    for (const part of parts) {
      const num = parseInt(part, 10);
      if (isNaN(num) || num <= 0 || !Number.isInteger(parseFloat(part))) {
        throw new Error(t('errorInvalidNumber', { number: part }));
      }
      numbers.push(num);
    }

    if (numbers.length < 2) {
      throw new Error(t.raw('errorMinNumbers'));
    }

    return numbers;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const numbers = parseInput(numbersInput);
      const commonFactors = getCommonFactors(numbers);
      const gcf = calculateGCF(numbers);

      const numbersWithFactors = numbers.map(num => ({
        value: num,
        factors: getFactors(num)
      }));

      setResults({
        gcf,
        commonFactors,
        numbers: numbersWithFactors
      });
    } catch (err: any) {
      setError(err.message);
      setResults(null);
    }
  };

  const handleClear = () => {
    setNumbersInput('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)', borderRadius: '1rem', marginBottom: '2rem', boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)' },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const textareaStyle = getResponsiveStyle(
    { width: '100%', border: '2px solid #e2e8f0', borderRadius: '0.5rem', transition: 'all 0.3s ease', fontFamily: "'Courier New', monospace", resize: 'vertical' as const, minHeight: '100px', boxSizing: 'border-box' as const },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const helperTextStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    { flex: 1, border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    { flex: 1, border: 'none', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease', background: '#f1f5f9', color: '#475569' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    { background: '#fee2e2', color: '#991b1b', borderRadius: '0.5rem', marginTop: '1rem', borderLeft: '4px solid #dc2626' },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderTop: '3px solid #f59e0b' },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const gcfHighlightStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '0.5rem', marginBottom: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const gcfLabelStyle = getResponsiveStyle(
    { display: 'block', color: 'white', fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const gcfValueStyle = getResponsiveStyle(
    { display: 'block', color: 'white', fontWeight: 700 },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' },
    { fontSize: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.5rem', marginBottom: '1rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const factorsListStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginTop: '0.75rem'
  };

  const factorBadgeStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', borderRadius: '2rem', fontWeight: 600 },
    { padding: '0.5rem 0.875rem', fontSize: '0.95rem' },
    { padding: '0.5rem 1rem', fontSize: '1rem' },
    { padding: '0.5rem 1rem', fontSize: '1rem' }
  );

  const numberInfoStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginTop: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const numberCardStyle = getResponsiveStyle(
    { background: '#f8fafc', borderRadius: '0.5rem', border: '1px solid #e2e8f0' },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1rem' }
  );

  const numberCardTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#475569', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const numberCardValueStyle = getResponsiveStyle(
    { color: '#2563eb', fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const numberCardFactorsStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.85rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    { background: colors.glassBg, backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', border: `1px solid ${colors.glassBorder}`, borderRadius: '1rem', boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)', marginBottom: '2rem' },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const referenceListStyle = {
    listStyle: 'none' as const,
    padding: 0
  };

  const referenceItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' as const },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none' as const,
    wordBreak: 'break-word' as const,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t.raw('title')}</h1>
        <p style={summaryTextStyle}>{t.raw('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🧮 {t.raw('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="numbers-input">
                {t.raw('inputLabel')}
              </label>
              <textarea
                id="numbers-input"
                style={textareaStyle}
                placeholder={t.raw('inputPlaceholder')}
                value={numbersInput}
                onChange={(e) => setNumbersInput(e.target.value)}
                required
              />
              <div style={helperTextStyle}>{t.raw('helperText')}</div>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>
                {t.raw('calculateButton')}
              </button>
              <button type="button" style={clearButtonStyle} onClick={handleClear}>
                {t.raw('clearButton')}
              </button>
            </div>

            {error && (
              <div style={errorMessageStyle}>{error}</div>
            )}
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t.raw('resultsTitle')}</h3>

              <div style={gcfHighlightStyle}>
                <span style={gcfLabelStyle}>{t.raw('gcfLabel')}</span>
                <span style={gcfValueStyle}>{results.gcf}</span>
              </div>

              <div style={resultItemStyle}>
                <span style={resultLabelStyle}>{t.raw('commonFactorsLabel')}</span>
                <div style={factorsListStyle}>
                  {results.commonFactors.map((factor, index) => (
                    <span key={index} style={factorBadgeStyle}>
                      {factor}
                    </span>
                  ))}
                </div>
              </div>

              <div style={numberInfoStyle}>
                {results.numbers.map((num, index) => (
                  <div key={index} style={numberCardStyle}>
                    <div style={numberCardTitleStyle}>
                      {
                        t.raw('numberLabel').replace('{{index}}', (index + 1).toString()).replace('{{value}}', num.value.toString())
                      }
                    </div>
                    <div style={numberCardValueStyle}>
                      {
                        t.raw('factorsCount').replace('{{count}}', num.factors.length.toString())
                      }
                    </div>
                    <div style={numberCardFactorsStyle}>
                      {num.factors.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t.raw('introTitle')}</h2>
        <p style={paragraphStyle}>{t.raw('introParagraph1')}</p>
        <p style={paragraphStyle}>{t.raw('introParagraph2')}</p>
        <p style={paragraphStyle}>{t.raw('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t.raw('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('step1Title')}</strong> {t.raw('step1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('step2Title')}</strong> {t.raw('step2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('step3Title')}</strong> {t.raw('step3Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('step4Title')}</strong> {t.raw('step4Description')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t.raw('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t.raw('insight1')}</p>
        <p style={paragraphStyle}>{t.raw('insight2')}</p>
        <p style={paragraphStyle}>{t.raw('insight3')}</p>
        <p style={paragraphStyle}>{t.raw('insight4')}</p>
        <p style={paragraphStyle}>{t.raw('insight5')}</p>
        <p style={paragraphStyle}>{t.raw('insight6')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t.raw('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem' }}>
          {t.raw('methodsTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('method1Title')}</strong> {t.raw('method1Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('method2Title')}</strong> {t.raw('method2Description')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t.raw('method3Title')}</strong> {t.raw('method3Description')}
          </li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '2rem' }}>
          {t.raw('applicationsTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t.raw('application1')}</li>
          <li style={listItemStyle}>{t.raw('application2')}</li>
          <li style={listItemStyle}>{t.raw('application3')}</li>
          <li style={listItemStyle}>{t.raw('application4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t.raw('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem' }}>
          {t.raw('faq1Question')}
        </h3>
        <p style={paragraphStyle}>{t.raw('faq1Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t.raw('faq2Question')}
        </h3>
        <p style={paragraphStyle}>{t.raw('faq2Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t.raw('faq3Question')}
        </h3>
        <p style={paragraphStyle}>{t.raw('faq3Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t.raw('faq4Question')}
        </h3>
        <p style={paragraphStyle}>{t.raw('faq4Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : screenSize === 'tablet' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t.raw('faq5Question')}
        </h3>
        <p style={paragraphStyle}>{t.raw('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t.raw('referencesTitle')}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <a
              href="https://www.omnicalculator.com/math/gcf"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference1')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://byjus.com/maths/gcf/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference2')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.smartick.com/blog/mathematics/multiplication-and-division/greatest-common-factor-gcf/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference3')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.vedantu.com/maths/gcf"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference4')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.calculatorsoup.com/calculators/math/gcf.php"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference5')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.mathsisfun.com/definitions/common-factor.html"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference6')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://en.wikipedia.org/wiki/Greatest_common_divisor"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t.raw('reference7')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}